import invariant from 'tiny-invariant';
import {
  actions,
  ActorRefFrom,
  assign,
  createMachine,
  forwardTo,
  send,
  spawn,
} from 'xstate';
import { isRequestMessage } from '../helpers';
import { ServiceID, Message, Version } from '../types';
import { requestStop, sendResponse } from './actions';
import { request } from './requestActor';

let stop = actions.stop;
let pure = actions.pure;

type Context = {
  id: ServiceID;
  children: {
    [p: Version]: ActorRefFrom<typeof request>;
  };
};

type ServiceMessage = Message | { type: 'stop_working' };

export let service = createMachine<Context, ServiceMessage>(
  {
    id: 'service',
    meta: {
      description:
        __DEV__ &&
        'Отвечает за запросы ко всем версиям черновиков в рамках одной услуги',
    },
    context: {
      id: '',
      children: {},
    },
    initial: 'idle',
    states: {
      idle: {
        after: {
          1000: { actions: ['request_stop'] },
        },
        on: {
          save: {
            target: 'working',
            actions: ['forward_to_version_request'],
          },
          load: {
            target: 'working',
            actions: ['forward_to_version_request'],
          },
          delete: {
            target: 'working',
            actions: ['forward_to_version_request'],
          },
        },
      },
      working: {
        on: {
          save: {
            actions: ['forward_to_version_request'],
          },
          delete: {
            actions: ['forward_to_version_request'],
          },
          load: {
            actions: ['forward_to_version_request'],
          },
          done: { actions: ['send_response'] },
          stop_child: { actions: ['stop_child'] },
          stop_working: { target: 'idle' },
        },
      },
    },
  },
  {
    actions: {
      forward_to_version_request: pure((ctx, message) => {
        isRequestMessage(message);
        let actions = [];
        let id = message.version;
        let actorRef = ctx.children[id];
        if (!actorRef) {
          actorRef = spawn(request.withContext({ id, queue: [] }), id);
          actions.push(
            assign<Context, ServiceMessage>({
              children: (ctx) => ({ ...ctx.children, [id]: actorRef }),
            })
          );
        }
        actions.push(forwardTo<Context, ServiceMessage>(() => actorRef));
        return actions;
      }),
      stop_child: pure((ctx, message) => {
        invariant(message.type === 'stop_child');
        let actions = [];
        let { id } = message;
        let children = { ...ctx.children };
        if (id in ctx.children) {
          actions.push(stop<Context, ServiceMessage>(() => ctx.children[id]));
          delete children[id];
          actions.push(assign<Context, ServiceMessage>({ children }));
        }
        if (Object.keys(children).length === 0) {
          actions.push(send<Context, ServiceMessage>('stop_working'));
        }
        return actions;
      }),
      send_response: sendResponse,
      request_stop: requestStop,
    },
  }
);
